import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
import { toast } from "../common_components/Toast/ToastUtils";
// initial states
const defaultPagination = {
    total: 0,
    page: 1,
    limit: 10,
    total_pages: 1,
};
const initialState = {
    errorLogs: [],
    errorLogPagination: defaultPagination,
    errorLogLoading: false,
    errorLogError: null,
    errorLogTypes: [],
    syncLogs: [],
    syncLogPagination: defaultPagination,
    syncLogLoading: false,
    syncLogError: null,
    syncLogTypes: [],
    syncLogClients: [],
    latestFetchRequestId: null,
    saving: false,
    saveError: null,
    deleting: false,
    deleteError: null,
};
// helpers
const buildQueryString = (params) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "")
            qs.set(k, String(v));
    });
    const str = qs.toString();
    return str ? `?${str}` : "";
};
// Filter-options thunk — populates error/sync type vocabularies + the header's client list in one call.
export const fetchSyncLogTypes = createAsyncThunk("syncLog/fetchSyncLogTypes", async (arg, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token;
        const status = arg?.status === 'inactive' ? 'inactive' : 'active';
        const response = await ApiCall("GET", `/api/process-logs/filter-options?status=${status}`, undefined, { Authorization: `Bearer ${token}`, "x-module-key": "sync_log" });
        if (response && "data" in response && response.data?.success) {
            const payload = response.data.data || {};
            return {
                errorTypes: payload.error_types || [],
                syncTypes: payload.sync_types || [],
                clients: payload.clients || [],
            };
        }
        return rejectWithValue(response?.data?.message || "Failed to fetch sync log types");
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message || "Failed to fetch sync log types");
    }
});
export const updateSyncLog = createAsyncThunk("syncLog/updateSyncLog", async ({ id, ...payload }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token;
        const response = await ApiCall("PATCH", `/api/process-logs/sync-logs/${id}`, payload, {
            Authorization: `Bearer ${token}`,
            "x-module-key": "sync_log",
        });
        if (response && "data" in response && response.data?.success) {
            toast.success("Sync log updated successfully");
            return response.data.data;
        }
        const msg = response?.data?.message || "Failed to update sync log";
        toast.error(msg);
        return rejectWithValue(msg);
    }
    catch (error) {
        const msg = error?.response?.data?.message || "Failed to update sync log";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
// One request creates per-network chunked sync logs; returns a { created_count, skipped } summary.
export const createBackfillSyncLogs = createAsyncThunk("syncLog/createBackfillSyncLogs", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token;
        const response = await ApiCall("POST", "/api/process-logs/sync-logs/backfill", payload, {
            Authorization: `Bearer ${token}`,
            "x-module-key": "sync_log",
        });
        if (response && "data" in response && response.data?.success) {
            const d = response.data.data || {};
            toast.success(response.data.message || `${d.created_count ?? 0} sync log(s) created successfully`);
            return { created_count: d.created_count ?? 0, skipped: d.skipped ?? [] };
        }
        const msg = response?.data?.message || "Failed to create sync logs";
        toast.error(msg);
        return rejectWithValue(msg);
    }
    catch (error) {
        const msg = error?.response?.data?.message || "Failed to create sync logs";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
export const deleteErrorLog = createAsyncThunk("syncLog/deleteErrorLog", async (ids, { getState, rejectWithValue }) => {
    if (!ids || ids.length === 0)
        return rejectWithValue("No items selected");
    try {
        const token = getState().auth.token;
        const response = await ApiCall("DELETE", "/api/process-logs/error-logs/delete", { ids }, { Authorization: `Bearer ${token}`, "x-module-key": "sync_log" });
        if (response && "data" in response && response.data?.success) {
            const deletedCount = response.data.data?.deleted_count ?? ids.length;
            toast.success(`Successfully deleted ${ids.length} error log${ids.length === 1 ? "" : "s"}.`);
            return { deletedIds: ids, deletedCount };
        }
        const msg = response?.data?.message || "Failed to delete error logs";
        toast.error(msg);
        return rejectWithValue(msg);
    }
    catch (error) {
        const msg = error?.response?.data?.message || "Failed to delete error logs";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
export const deleteSyncLog = createAsyncThunk("syncLog/deleteSyncLog", async (ids, { getState, rejectWithValue }) => {
    if (!ids || ids.length === 0)
        return rejectWithValue("No items selected");
    try {
        const token = getState().auth.token;
        const response = await ApiCall("DELETE", "/api/process-logs/sync-logs/delete", { ids }, { Authorization: `Bearer ${token}`, "x-module-key": "sync_log" });
        if (response && "data" in response && response.data?.success) {
            const deletedCount = response.data.data?.deleted_count ?? ids.length;
            toast.success(`Successfully deleted ${ids.length} sync log${ids.length === 1 ? "" : "s"}.`);
            return { deletedIds: ids, deletedCount };
        }
        const msg = response?.data?.message || "Failed to delete sync logs";
        toast.error(msg);
        return rejectWithValue(msg);
    }
    catch (error) {
        const msg = error?.response?.data?.message || "Failed to delete sync logs";
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
export const fetchSyncLogs = createAsyncThunk("syncLog/fetchSyncLogs", async (params, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token;
        const qs = buildQueryString(params);
        const response = await ApiCall("GET", `/api/process-logs/list${qs}`, undefined, { Authorization: `Bearer ${token}`, "x-module-key": "sync_log" });
        if (response && "data" in response && response.data?.success) {
            const payload = response.data.data || {};
            const errorBlock = payload.error_logs || {};
            const syncBlock = payload.sync_logs || {};
            return {
                errorLogs: errorBlock.data || [],
                errorPagination: errorBlock.pagination || defaultPagination,
                syncLogs: syncBlock.data || [],
                syncPagination: syncBlock.pagination || defaultPagination,
            };
        }
        return rejectWithValue(response?.data?.message || "Failed to fetch sync logs");
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message || "Failed to fetch sync logs");
    }
});
// slice
const syncLogSlice = createSlice({
    name: "syncLog",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Fetch combined sync log types (single call → fills both type dropdowns)
        builder
            .addCase(fetchSyncLogTypes.fulfilled, (state, action) => {
            state.errorLogTypes = action.payload.errorTypes;
            state.syncLogTypes = action.payload.syncTypes;
            state.syncLogClients = action.payload.clients;
        });
        // Fetch combined sync logs (single round trip → fills both halves)
        builder
            .addCase(fetchSyncLogs.pending, (state, action) => {
            // Mark this as the active request; only its result will be accepted.
            state.latestFetchRequestId = action.meta.requestId;
            state.errorLogLoading = true;
            state.syncLogLoading = true;
            state.errorLogError = null;
            state.syncLogError = null;
        })
            .addCase(fetchSyncLogs.fulfilled, (state, action) => {
            // Drop stale, out-of-order responses — a slower older request must not overwrite
            // the results of a newer one (e.g. fast filter/search/page changes).
            if (action.meta.requestId !== state.latestFetchRequestId)
                return;
            state.errorLogLoading = false;
            state.syncLogLoading = false;
            state.errorLogs = action.payload.errorLogs;
            state.errorLogPagination = action.payload.errorPagination;
            state.syncLogs = action.payload.syncLogs;
            state.syncLogPagination = action.payload.syncPagination;
        })
            .addCase(fetchSyncLogs.rejected, (state, action) => {
            // Ignore a stale rejection so it can't clear the loading flag while a newer
            // request is still in flight.
            if (action.meta.requestId !== state.latestFetchRequestId)
                return;
            state.errorLogLoading = false;
            state.syncLogLoading = false;
            const msg = action.payload || "Failed to fetch sync logs";
            state.errorLogError = msg;
            state.syncLogError = msg;
        });
        // Backfill creation — just toggles saving; rows arrive via the caller's refetch.
        builder
            .addCase(createBackfillSyncLogs.pending, (state) => {
            state.saving = true;
            state.saveError = null;
        })
            .addCase(createBackfillSyncLogs.fulfilled, (state) => {
            state.saving = false;
        })
            .addCase(createBackfillSyncLogs.rejected, (state, action) => {
            state.saving = false;
            state.saveError = action.payload || "Failed to create sync logs";
        });
        // Update Sync Log
        builder
            .addCase(updateSyncLog.pending, (state) => {
            state.saving = true;
            state.saveError = null;
        })
            .addCase(updateSyncLog.fulfilled, (state, action) => {
            state.saving = false;
            // The update endpoint returns the raw sync-log document, while the list rows are
            // formatted/aggregated objects (client_name, account_name, grouped type, formatted
            // dates, etc.). Spreading the raw doc would override those display fields — including
            // sync `type` — with raw values. Only patch the fields the edit can actually change.
            const { _id, priority, status, error } = action.payload;
            state.syncLogs = state.syncLogs.map((log) => log._id === _id ? { ...log, priority, status, error } : log);
        })
            .addCase(updateSyncLog.rejected, (state, action) => {
            state.saving = false;
            state.saveError = action.payload || "Failed to update sync log";
        });
        // Delete Error Logs (single or many)
        builder
            .addCase(deleteErrorLog.pending, (state) => {
            state.deleting = true;
            state.deleteError = null;
        })
            .addCase(deleteErrorLog.fulfilled, (state, action) => {
            state.deleting = false;
            const removed = new Set(action.payload.deletedIds);
            state.errorLogs = state.errorLogs.filter((log) => !removed.has(log._id));
            state.errorLogPagination.total = Math.max(0, state.errorLogPagination.total - action.payload.deletedIds.length);
        })
            .addCase(deleteErrorLog.rejected, (state, action) => {
            state.deleting = false;
            state.deleteError = action.payload || "Failed to delete error logs";
        });
        // Delete Sync Logs (single or many)
        builder
            .addCase(deleteSyncLog.pending, (state) => {
            state.deleting = true;
            state.deleteError = null;
        })
            .addCase(deleteSyncLog.fulfilled, (state, action) => {
            state.deleting = false;
            const removed = new Set(action.payload.deletedIds);
            state.syncLogs = state.syncLogs.filter((log) => !removed.has(log._id));
            state.syncLogPagination.total = Math.max(0, state.syncLogPagination.total - action.payload.deletedIds.length);
        })
            .addCase(deleteSyncLog.rejected, (state, action) => {
            state.deleting = false;
            state.deleteError = action.payload || "Failed to delete sync logs";
        });
    },
});
// selectors
export const selectErrorLogs = (state) => state.syncLog.errorLogs;
export const selectErrorLogPagination = (state) => state.syncLog.errorLogPagination;
export const selectErrorLogLoading = (state) => state.syncLog.errorLogLoading;
export const selectErrorLogTypes = (state) => state.syncLog.errorLogTypes;
export const selectSyncLogs = (state) => state.syncLog.syncLogs;
export const selectSyncLogPagination = (state) => state.syncLog.syncLogPagination;
export const selectSyncLogLoading = (state) => state.syncLog.syncLogLoading;
export const selectSyncLogTypes = (state) => state.syncLog.syncLogTypes;
export const selectSyncLogClients = (state) => state.syncLog.syncLogClients;
export const selectSyncLogSaving = (state) => state.syncLog.saving;
export default syncLogSlice.reducer;
